/**
 * Painel de uso de IA (01/11, ficha `2026-10-30_malgo_FUN_painel-uso-de-ia`): Admin > Painel admin > Uso de IA.
 *
 * Tabela de preços OFICIAL da Anthropic (https://platform.claude.com/docs/en/about-claude/pricing, conferida em 01/11/2026),
 * em US$ por milhão de tokens. O custo é o equivalente em preço de API (o plano do Claude Code é cobrado de outro jeito):
 * serve para comparar dias, modelos e projetos, não é a fatura. Preço muda: corrigir AQUI e a tela recalcula o histórico
 * inteiro (o banco guarda só tokens, nunca valor).
 *
 * Regras da página (01/11/2026):
 *  - escrita de cache: 1,25× a entrada (TTL de 5 min) e 2× (TTL de 1 h); leitura de cache: 0,1× (0,05× no Opus 5.5 e no
 *    Sonnet 5.5; 0,025× no Fable 5.1 e no Mythos 5.1) — já embutido nos números da tabela abaixo;
 *  - fast mode (`speed: "fast"`): Opus 5.5 a $8/$40, Opus 5 e Opus 4.8 a $10/$50; os multiplicadores de cache valem sobre
 *    o preço do fast mode;
 *  - Haiku 5.5: o pedido com prompt acima de 100.000 tokens (entrada + escrita e leitura de cache) paga a faixa longa;
 *  - `inference_geo: "us"` multiplica tudo por 1,1 (modelos 4.6 em diante);
 *  - contexto de 1M sem acréscimo nos modelos 4.6 em diante (fora o Haiku 5.5, acima).
 */
export const FONTE_PRECOS_USO_IA = 'https://platform.claude.com/docs/en/about-claude/pricing';
export const PRECOS_CONFERIDOS_EM = '2026-11-01';

/** US$ por milhão de tokens. */
export interface PrecoModelo {
  entrada: number;
  cache5m: number;
  cache1h: number;
  cacheLeitura: number;
  saida: number;
}

const p = (entrada: number, cache5m: number, cache1h: number, cacheLeitura: number, saida: number): PrecoModelo => ({ entrada, cache5m, cache1h, cacheLeitura, saida });

/** Chave = id do modelo sem sufixo de data. Ordem: do mais específico ao mais geral não importa (casamento exato). */
export const PRECOS_USO_IA: Readonly<Record<string, PrecoModelo>> = {
  'claude-fable-5-1': p(10, 12.5, 20, 0.25, 50),
  'claude-mythos-5-1': p(10, 12.5, 20, 0.25, 50),
  'claude-fable-5': p(10, 12.5, 20, 1, 50),
  'claude-mythos-5': p(10, 12.5, 20, 1, 50),
  'claude-opus-5-5': p(4, 5, 8, 0.2, 20),
  'claude-opus-5': p(5, 6.25, 10, 0.5, 25),
  'claude-opus-4-8': p(5, 6.25, 10, 0.5, 25),
  'claude-opus-4-7': p(5, 6.25, 10, 0.5, 25),
  'claude-opus-4-6': p(5, 6.25, 10, 0.5, 25),
  'claude-opus-4-5': p(5, 6.25, 10, 0.5, 25),
  'claude-opus-4-1': p(15, 18.75, 30, 1.5, 75),
  'claude-opus-4': p(15, 18.75, 30, 1.5, 75),
  'claude-sonnet-5-5': p(2, 2.5, 4, 0.1, 10),
  'claude-sonnet-5': p(2, 2.5, 4, 0.2, 10),
  'claude-sonnet-4-6': p(3, 3.75, 6, 0.3, 15),
  'claude-sonnet-4-5': p(3, 3.75, 6, 0.3, 15),
  'claude-sonnet-4': p(3, 3.75, 6, 0.3, 15),
  'claude-haiku-5-5': p(0.1, 0.125, 0.2, 0.01, 0.5),
  'claude-haiku-4-5': p(1, 1.25, 2, 0.1, 5),
  'claude-3-5-haiku': p(0.8, 1, 1.6, 0.08, 4),
};

/** Haiku 5.5 com prompt acima de 100.000 tokens (por pedido). */
export const PRECOS_USO_IA_LONGO: Readonly<Record<string, PrecoModelo>> = {
  'claude-haiku-5-5': p(0.5, 0.625, 1, 0.05, 2.5),
};
export const LIMITE_PROMPT_LONGO = 100_000;

/** Fast mode: entrada e saída; o cache segue os multiplicadores sobre a entrada do fast mode (1,25×, 2× e o da leitura do modelo). */
export const PRECOS_USO_IA_RAPIDO: Readonly<Record<string, { entrada: number; saida: number }>> = {
  'claude-opus-5-5': { entrada: 8, saida: 40 },
  'claude-opus-5': { entrada: 10, saida: 50 },
  'claude-opus-4-8': { entrada: 10, saida: 50 },
};

/** Modelos em que `inference_geo: "us"` custa 1,1× (4.6 em diante). */
const SEM_MULTIPLICADOR_GEO = new Set(['claude-opus-4-5', 'claude-opus-4-1', 'claude-opus-4', 'claude-sonnet-4-5', 'claude-sonnet-4', 'claude-haiku-4-5', 'claude-3-5-haiku']);
export const MULTIPLICADOR_GEO_US = 1.1;

/** Tira o sufixo de data (`-20251001`), o marcador de contexto (`[1m]`) e o prefixo de provedor (`anthropic.`, `us.anthropic.`). */
export function modeloNormalizado(modelo: string): string {
  return modelo
    .trim()
    .toLowerCase()
    .replace(/^(?:[a-z]{2}\.)?anthropic\./, '')
    .replace(/\[[^\]]*\]$/, '')
    .replace(/-v\d+(?::\d+)?$/, '')
    .replace(/[-@]\d{8}$/, '');
}

export const TARIFAS_USO_IA = ['padrao', 'rapido', 'longo'] as const;
export type TarifaUsoIa = (typeof TARIFAS_USO_IA)[number];
export const TIPOS_USO_IA = ['principal', 'agente'] as const;
export type TipoUsoIa = (typeof TIPOS_USO_IA)[number];

export interface TokensUsoIa {
  entrada: number;
  cache5m: number;
  cache1h: number;
  cacheLeitura: number;
  saida: number;
}

/** Preço efetivo por milhão (já com a tarifa e a geografia), ou `null` quando o modelo não está na tabela. */
export function precoEfetivo(modelo: string, tarifa: TarifaUsoIa, geoUs: boolean): PrecoModelo | null {
  const m = modeloNormalizado(modelo);
  const base = (tarifa === 'longo' ? PRECOS_USO_IA_LONGO[m] : undefined) ?? PRECOS_USO_IA[m];
  if (!base) return null;
  let preco = base;
  const rapido = tarifa === 'rapido' ? PRECOS_USO_IA_RAPIDO[m] : undefined;
  if (rapido) {
    // A leitura de cache mantém a razão do modelo (0,05× no Opus 5.5) sobre a entrada do fast mode.
    const razaoLeitura = base.cacheLeitura / base.entrada;
    preco = p(rapido.entrada, rapido.entrada * 1.25, rapido.entrada * 2, rapido.entrada * razaoLeitura, rapido.saida);
  }
  if (geoUs && !SEM_MULTIPLICADOR_GEO.has(m)) {
    const k = MULTIPLICADOR_GEO_US;
    preco = p(preco.entrada * k, preco.cache5m * k, preco.cache1h * k, preco.cacheLeitura * k, preco.saida * k);
  }
  return preco;
}

/** Custo em US$ (número com casas decimais), ou `null` quando o modelo não tem preço na tabela. */
export function custoUsoIaUsd(modelo: string, tarifa: TarifaUsoIa, geoUs: boolean, t: TokensUsoIa): number | null {
  const preco = precoEfetivo(modelo, tarifa, geoUs);
  if (!preco) return null;
  return (t.entrada * preco.entrada + t.cache5m * preco.cache5m + t.cache1h * preco.cache1h + t.cacheLeitura * preco.cacheLeitura + t.saida * preco.saida) / 1_000_000;
}

/** Envio do medidor (`scripts/uso-ia/medir.mjs`): tetos do lote (o esquema fica na API, `apps/api/src/nucleo/uso-ia.ts`). */
export const MAX_DIAS_ENVIO_USO_IA = 400;
export const MAX_LINHAS_DIA_USO_IA = 500;

export const GRANULARIDADES_USO_IA = ['dia', 'semana', 'mes', 'ano'] as const;
export type GranularidadeUsoIa = (typeof GRANULARIDADES_USO_IA)[number];
/** Quantos períodos a série mostra em cada granularidade. */
export const PERIODOS_SERIE_USO_IA: Record<GranularidadeUsoIa, number> = { dia: 30, semana: 12, mes: 12, ano: 5 };
